
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import range from 'lodash/range';
import uniq from 'lodash/uniq';
import { defineStore } from 'pinia';
import { PartialDeep } from 'type-fest';

import { DayOfWeek } from '@evidentid/dashboard-commons/components/DayOfWeekInput/types';
import { getPersistingErrorActions } from '@evidentid/dashboard-commons/modules/persisting-error';
import { GatewayApiClient } from '@evidentid/gateway-api-client';
import {
    BrandingInput,
    ConfigRelyingPartyInput,
    NotificationName,
    SupportTimeframe,
} from '@evidentid/gateway-api-client/src/models';
import type RpWebApiClient from '@evidentid/rpweb-api-client';
import { TprmRequestsConfig } from '@evidentid/tprm-portal-lib/models/common/TprmRequestsConfig.model';
import {
    NotificationAudience,
    NotificationConfiguration,
    NotificationType,
} from '@evidentid/tprm-portal-lib/models/notification-configuration';
import {
    TprmClientConfigInput,
} from '@evidentid/tprm-portal-lib/models/notification-configuration/TprmClientConfig.model';
import {
    VerificationRequestType,
} from '@evidentid/tprm-portal-lib/models/notification-configuration/VerificationRequest.model';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

import {
    evidentFulfillmentConfiguration,
    marshFulfillmentConfiguration,
} from '@/modules/notifications-configuration/constants';
import { buildCadenceInput } from '@/modules/notifications-configuration/utils/buildCadenceInput';
import { extractFulfillmentConfig } from '@/modules/notifications-configuration/utils/extractFulfillmentConfig';
import {
    getNotificationType,
} from '@/modules/notifications-configuration/utils/getNotificationTypeByNotificationName';

import {
    NotificationCadence,
    NotificationsConfiguration,
    NotificationsConfigurationState,
    ProfileConfiguration,
} from './types';
import { buildNotificationCadence } from './utils/buildNotificationCadence';

interface NotificationsConfigurationRequirements {
    gatewayClient: GatewayApiClient;
}

// Actions reach the Application through the $app Pinia plugin; typed here once.
function appOf(store: { $app: any }): NotificationsConfigurationRequirements {
    return store.$app;
}

const state = (): NotificationsConfigurationState => ({
    settings: {},
    loadSettingsStatus: {},
    updateSettingsStatus: {},
    emailTemplates: {},
    producerEmailTemplates: {},
    loadEmailTemplatesStatus: {},
    updateEmailTemplates: {},
    loadInsuredFields: {},
    insuredFields: {},
    cadences: {},
    loadCadences: {},
    updateCadences: {},
    sendTestEmailStatus: {},
});

function buildInsuranceConfigInput(config: NotificationsConfiguration): TprmClientConfigInput {
    return {
        displayName: config.displayName,
        certificateHolderAddress: config.certificateHolderAddress || null,
    };
}

function buildInsuranceRequestsConfigInput(config: NotificationsConfiguration): PartialDeep<TprmRequestsConfig> {
    return {
        coiRequirementsLink: config.coiRequirementsLink || null,
        fulfillmentUrl: config.legacyFulfillmentUrl || null,
    };
}

function buildSupportTimeframes(config: ProfileConfiguration): SupportTimeframe[] {
    if (!config.supportDaysOfWeek || !config.supportStartTime || !config.supportEndTime || !config.supportTimezone) {
        return [];
    }
    const name = 'support-hours';
    const description = '';
    const enabled = true;
    const supportHours = `${config.supportStartTime} - ${config.supportEndTime}`;
    const supportTimezone = config.supportTimezone;
    const supportDaysOfWeek = config.supportDaysOfWeek.slice().sort();
    const supportDaysOfWeekGroups: { start: DayOfWeek, end: DayOfWeek }[] = [];
    let currentStart: DayOfWeek | null = null;
    let currentEnd: DayOfWeek | null = null;
    for (const dayOfWeek of supportDaysOfWeek) {
        if (currentStart === null) {
            currentStart = dayOfWeek;
            currentEnd = dayOfWeek;
        } else if (dayOfWeek === currentEnd! + 1) {
            currentEnd = dayOfWeek;
        } else {
            supportDaysOfWeekGroups.push({ start: currentStart, end: currentEnd! });
            currentStart = dayOfWeek;
            currentEnd = dayOfWeek;
        }
    }
    if (currentStart !== null) {
        supportDaysOfWeekGroups.push({ start: currentStart, end: currentEnd! });
    }
    return supportDaysOfWeekGroups.map(({ start, end }, index) => ({
        name: `${name}-${index}`,
        description,
        enabled,
        supportHours,
        supportTimezone,
        supportDays: `${start}-${end}`,
    }));
}

function buildProfileConfigInput(config: NotificationsConfiguration): Partial<ConfigRelyingPartyInput> {
    return {
        displayName: config.displayName,
        wordmarkUri: config.wordmarkUrl,
        primaryColor: config.primaryColor,
        logoUrl: config.iconUri,
        supportUrl: config.supportUrl || null,
        supportUrlTitle: config.supportUrlTitle || null,
        supportPhone: config.supportPhone || null,
        supportEmail: config.supportEmail || null,
        supportTimeframes: buildSupportTimeframes(config),
    };
}

function buildBrandingInput(config: NotificationsConfiguration): Partial<BrandingInput> {
    return {
        fromName: config.fromName,
        replyToName: config.replyToName,
        replyToEmail: config.replyToEmail,
        boxBehindButtonBgColor: config.primaryColor,
        buttonBgColor: config.primaryColor,
        barBelowWordmarkBgColor: config.primaryColor,
        boxBehindButtonFgColor: config.secondaryColor,
        buttonFgColor: config.secondaryColor,
    };
}

export const useNotificationsConfigurationStore = defineStore('notificationsConfiguration', {
    state,
    actions: {
        setSettings(payload: { rpName: string, settings: NotificationsConfiguration }) {
            this.settings = {
                ...this.settings,
                [payload.rpName]: payload.settings,
            };
        },
        setLoadSettingsStatus(payload: { rpName: string, status: OperationStatus }) {
            this.loadSettingsStatus = {
                ...this.loadSettingsStatus,
                [payload.rpName]: payload.status,
            };
        },
        setUpdateSettingsStatus(payload: { rpName: string, status: OperationStatus }) {
            this.updateSettingsStatus = {
                ...this.updateSettingsStatus,
                [payload.rpName]: payload.status,
            };
        },
        setEmailTemplates(payload: {
            rpName: string;
            emailTemplates: Record<NotificationType, NotificationConfiguration>;
            producerEmailTemplates?: Partial<Record<NotificationType, NotificationConfiguration>>;
        }) {
            this.emailTemplates = {
                ...this.emailTemplates,
                [payload.rpName]: payload.emailTemplates,
            };
            this.producerEmailTemplates = {
                ...this.producerEmailTemplates,
                [payload.rpName]: payload.producerEmailTemplates || {},
            };
        },
        setLoadEmailTemplatesStatus(payload: { rpName: string, status: OperationStatus }) {
            this.loadEmailTemplatesStatus = {
                ...this.loadEmailTemplatesStatus,
                [payload.rpName]: payload.status,
            };
        },
        setUpdateEmailTemplatesStatus(payload: {
            rpName: string;
            type: NotificationType;
            status: OperationStatus;
        }) {
            this.updateEmailTemplates = {
                ...this.updateEmailTemplates,
                [payload.rpName]: {
                    ...this.updateEmailTemplates[payload.rpName],
                    [payload.type]: payload.status,
                },
            };
        },
        setLoadcustomPropertiesStatus(payload: { rpName: string, status: OperationStatus }) {
            this.loadInsuredFields = {
                ...this.loadInsuredFields,
                [payload.rpName]: payload.status,
            };
        },
        setInsuredFields(payload: { rpName: string, insuredFields: string[] }) {
            this.insuredFields = {
                ...this.insuredFields,
                [payload.rpName]: payload.insuredFields,
            };
        },
        setNotificationCadence(payload: {
            rpName: string;
            type: NotificationName;
            cadence: NotificationCadence;
        }) {
            this.cadences = {
                ...this.cadences,
                [payload.rpName]: {
                    ...this.cadences[payload.rpName],
                    [payload.type]: payload.cadence,
                },
            };
        },
        setLoadNotificationCadenceStatus(payload: {
            rpName: string;
            type: NotificationName;
            status: OperationStatus;
        }) {
            this.loadCadences = {
                ...this.loadCadences,
                [payload.rpName]: {
                    ...this.loadCadences[payload.rpName],
                    [payload.type]: payload.status,
                },
            };
        },
        setUpdateNotificationCadenceStatus(payload: {
            rpName: string;
            type: NotificationName;
            status: OperationStatus;
        }) {
            this.updateCadences = {
                ...this.updateCadences,
                [payload.rpName]: {
                    ...this.updateCadences[payload.rpName],
                    [payload.type]: payload.status,
                },
            };
        },
        setSendTestEmailStatus(payload: {
            rpName: string;
            status: OperationStatus;
        }) {
            this.sendTestEmailStatus = {
                ...this.sendTestEmailStatus,
                [payload.rpName]: payload.status,
            };
        },
        clearSendTestEmailStatus(rpName: string) {
            this.sendTestEmailStatus = omit(this.sendTestEmailStatus, [ rpName ]);
        },

        async loadSettings(payload: { rpName: string }) {
            const { rpName } = payload;
            try {
                this.setLoadSettingsStatus({ rpName, status: OperationStatus.loading });
                const [
                    insuranceConfig,
                    insuranceRequestsConfig,
                    rpConfig,
                    [ brandingConfig ],
                ] = await Promise.all([
                    appOf(this).gatewayClient.tprmUi().getInsuranceConfig(rpName),
                    appOf(this).gatewayClient.tprmUi().getTprmRequestsConfig(rpName),
                    appOf(this).gatewayClient.tprmUi().getConfig(rpName),
                    appOf(this).gatewayClient.tprmUi().getBrandings(rpName),
                ]);
                let fulfillmentConfig = null;
                try {
                    fulfillmentConfig = await appOf(this).gatewayClient.tprmUi().getFulfillmentConfig(rpName);
                } catch (error) {
                // silent the not found error, it will be handled in NotificationEmailConfiguration with defaults
                    if (error.reason !== 'not-found') {
                        throw error;
                    }
                    fulfillmentConfig = insuranceRequestsConfig.fulfillmentUrl
                        ? {
                            ...marshFulfillmentConfiguration,
                            fulfillmentUrl: insuranceRequestsConfig.fulfillmentUrl,
                            enabled: true,
                        }
                        : { ...evidentFulfillmentConfiguration, enabled: false };
                }
                // Validate and parse support timeframes
                const timeframes = rpConfig.supportTimeframes
                    .filter((x) => x.enabled);
                const supportDays = timeframes
                    .filter((time) => /^([1-7])\s*-\s*([1-7])$/.test(time.supportDays))
                    .map((time) => time.supportDays.split(/\s*-\s*/))
                    .flatMap(([ start, end ]) => range(parseInt(start, 10), parseInt(end, 10) + 1) as DayOfWeek[])
                    .filter((x, i, a) => a.indexOf(x) === i)
                    .sort();
                const rawSupportHours = timeframes
                    .map((x) => x.supportHours)
                    .filter((x, i, a) => a.indexOf(x) === i)
                    .filter((x) => /^((?:[0-1]?[0-9]|2[0-3]):[0-5][0-9])\s*-\s*((?:[0-1]?[0-9]|2[0-3]):[0-5][0-9])$/);
                const supportHours = rawSupportHours.length === 1 ? rawSupportHours[0].split(/\s*-\s*/) : null;
                const [ supportStartTime, supportEndTime ] = supportHours == null ? [ null, null ] : supportHours;
                const supportTimezones = uniq(timeframes.map((x) => x.supportTimezone));
                const supportTimezone = supportTimezones.length === 1 ? supportTimezones[0] : null;
                this.setSettings({
                    rpName,
                    settings: {
                        iconUri: rpConfig.iconUri,
                        displayName: rpConfig.displayName,
                        primaryColor: rpConfig.primaryColor,
                        secondaryColor: brandingConfig.buttonFgColor || brandingConfig.boxBehindButtonFgColor,
                        wordmarkUrl: rpConfig.wordmarkUri,
                        fromName: brandingConfig.fromName,
                        replyToEmail: brandingConfig.replyToEmail,
                        replyToName: brandingConfig.replyToName,
                        supportEmail: rpConfig.supportEmail,
                        supportPhone: rpConfig.supportPhone,
                        supportUrl: rpConfig.supportUrl,
                        supportUrlTitle: rpConfig.supportUrlTitle,
                        supportDaysOfWeek: supportDays,
                        supportStartTime,
                        supportEndTime,
                        supportTimezone,
                        certificateHolderAddress: insuranceConfig.certificateHolderAddress || '',
                        coiRequirementsLink: insuranceRequestsConfig.coiRequirementsLink,
                        legacyFulfillmentUrl: insuranceRequestsConfig.fulfillmentUrl,
                        fulfillmentConfiguration: extractFulfillmentConfig(fulfillmentConfig),
                    },
                });
                this.setLoadSettingsStatus({ rpName, status: OperationStatus.success });
            } catch (error) {
                this.setLoadSettingsStatus({ rpName, status: OperationStatus.error });
                await getPersistingErrorActions().showError(error);
            }
        },
        async updateSettings(payload: { rpName: string, settings: Partial<NotificationsConfiguration> }) {
            const { rpName } = payload;
            const prevConfig = this.settings?.[rpName];
            if (!prevConfig) {
                throw new Error('Tried to update unknown settings');
            }
            const newConfig = { ...prevConfig, ...payload.settings };

            const prevInsuranceConfig = buildInsuranceConfigInput(prevConfig);
            const newInsuranceConfig = buildInsuranceConfigInput(newConfig);
            const prevInsuranceRequestsConfig = buildInsuranceRequestsConfigInput(prevConfig);
            const newInsuranceRequestsConfig = buildInsuranceRequestsConfigInput(newConfig);
            const prevBrandingConfig = buildBrandingInput(prevConfig);
            const newBrandingConfig = buildBrandingInput(newConfig);
            const prevFulfillmentConfig = prevConfig.fulfillmentConfiguration;
            const newFulfillmentConfig = newConfig.fulfillmentConfiguration;
            const prevProfileConfig = buildProfileConfigInput(prevConfig);
            const newProfileConfig = buildProfileConfigInput(newConfig);
            // previous fulfillment config exists meaning there was data in the config
            // service already, removing legacy url
            if (prevFulfillmentConfig && prevFulfillmentConfig.fulfillmentUrl) {
                newInsuranceRequestsConfig.fulfillmentUrl = null;
            }

            try {
                this.setUpdateSettingsStatus({ rpName, status: OperationStatus.loading });
                await Promise.all([
                    isEqual(prevInsuranceConfig, newInsuranceConfig)
                        ? Promise.resolve()
                        : appOf(this).gatewayClient.tprmUi().updateInsuranceConfig(rpName, newInsuranceConfig),
                    isEqual(prevInsuranceRequestsConfig, newInsuranceRequestsConfig)
                        ? Promise.resolve()
                        : appOf(this).gatewayClient.tprmUi()
                            .updateTprmRequestsConfig(rpName, newInsuranceRequestsConfig),
                    isEqual(prevBrandingConfig, newBrandingConfig)
                        ? Promise.resolve()
                        : appOf(this).gatewayClient.tprmUi().updateDefaultBranding(rpName, newBrandingConfig),
                    isEqual(prevFulfillmentConfig, newFulfillmentConfig)
                        ? Promise.resolve()
                        : appOf(this).gatewayClient.tprmUi().updateFulfillmentConfig(rpName, newFulfillmentConfig),
                    isEqual(prevProfileConfig, newProfileConfig)
                        ? Promise.resolve()
                        : appOf(this).gatewayClient.tprmUi().updateConfig(rpName, newProfileConfig),
                ]);
                this.setSettings({ rpName, settings: newConfig });
                this.setUpdateSettingsStatus({ rpName, status: OperationStatus.success });
            } catch (error) {
                this.setUpdateSettingsStatus({ rpName, status: OperationStatus.error });
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadEmailTemplates(payload: { rpName: string, silent?: boolean }) {
            const { rpName, silent } = payload;
            try {
                this.setLoadEmailTemplatesStatus({ rpName, status: OperationStatus.loading });
                const { notifications, producerNotifications } = await appOf(this).gatewayClient
                    .tprmUi().getNotificationConfigurations(rpName);
                this.setEmailTemplates({
                    rpName,
                    emailTemplates: notifications,
                    producerEmailTemplates: producerNotifications,
                });
                this.setLoadEmailTemplatesStatus({ rpName, status: OperationStatus.success });
            } catch (error) {
                this.setLoadEmailTemplatesStatus({ rpName, status: OperationStatus.error });
                if (!silent) {
                    await getPersistingErrorActions().showError(error);
                }
            }
        },
        async updateEmailTemplate(payload: {
            rpName: string;
            type: NotificationType;
            template: NotificationConfiguration;
            audience?: NotificationAudience;
        }) {
            const { rpName, type, template, audience = NotificationAudience.entity } = payload;
            try {
                this.setUpdateEmailTemplatesStatus({ rpName, type, status: OperationStatus.loading });
                await appOf(this).gatewayClient.tprmUi()
                    .updateNotificationConfiguration(rpName, type, template, audience);
                // The editor measures edits against the stored text, so a retry after a later save fails
                // sends only what is still unsaved.
                if (audience === NotificationAudience.producer) {
                    this.producerEmailTemplates = {
                        ...this.producerEmailTemplates,
                        [rpName]: { ...this.producerEmailTemplates[rpName], [type]: template },
                    };
                } else {
                    this.emailTemplates = {
                        ...this.emailTemplates,
                        [rpName]: { ...this.emailTemplates[rpName], [type]: template },
                    };
                }
                this.setUpdateEmailTemplatesStatus({ rpName, type, status: OperationStatus.success });
            } catch (error) {
                this.setUpdateEmailTemplatesStatus({ rpName, type, status: OperationStatus.error });
                await getPersistingErrorActions().showError(error);
            }
        },
        async updateNotificationEnablement(payload: { rpName: string, type: NotificationType, initEnabled: boolean }) {
            const { rpName, type, initEnabled } = payload;
            try {
                this.setUpdateEmailTemplatesStatus({ rpName, type, status: OperationStatus.loading });
                // The PUT replaces the whole configuration, so read it back first: a template body
                // edited by someone else since this page loaded would otherwise be overwritten with
                // the stale copy held in the store.
                const { notifications } = await appOf(this).gatewayClient
                    .tprmUi().getNotificationConfigurations(rpName);
                const currentConfig = notifications[type];
                if (!currentConfig) {
                    throw new Error(`No notification configuration available for "${type}".`);
                }
                await appOf(this).gatewayClient.tprmUi().updateNotificationConfiguration(rpName, type, {
                    ...currentConfig,
                    initEnabled,
                });
                this.setUpdateEmailTemplatesStatus({ rpName, type, status: OperationStatus.success });
            } catch (error) {
                this.setUpdateEmailTemplatesStatus({ rpName, type, status: OperationStatus.error });
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadInsuredFieldIds(payload: { rpName: string }) {
            const { rpName } = payload;
            try {
                this.setLoadcustomPropertiesStatus({ rpName, status: OperationStatus.loading });
                const rawInsuredFields = await appOf(this).gatewayClient.tprmUi().getTprmCustomProperties(rpName);
                const insuredFields = rawInsuredFields.map((field) => field.key);
                this.setInsuredFields({ rpName, insuredFields });
                this.setLoadcustomPropertiesStatus({ rpName, status: OperationStatus.success });
            } catch (error) {
                this.setLoadcustomPropertiesStatus({ rpName, status: OperationStatus.error });
                await getPersistingErrorActions().showError(error);
            }
        },
        async loadNotificationCadence(payload: { rpName: string, type: NotificationName }) {
            const { rpName, type } = payload;
            try {
                this.setLoadNotificationCadenceStatus({ rpName, type, status: OperationStatus.loading });
                const [
                    { cadenceConfig },
                    { expirationNumberOfDays },
                    { notifications },
                ] = await Promise.all([
                    appOf(this).gatewayClient.tprmUi().getNotificationPreference(rpName, type),
                    appOf(this).gatewayClient.tprmUi().getTprmRequestsConfig(rpName),
                    appOf(this).gatewayClient.tprmUi().getNotificationConfigurations(rpName),
                ]);
                if (!cadenceConfig) {
                    throw new Error(`No cadence available for "${type}".`);
                }
                const notificationConfiguration = notifications[getNotificationType(type)];
                if (!notificationConfiguration) {
                    throw new Error(`No notification configuration available for "${type}".`);
                }
                const cadence = buildNotificationCadence({
                    cadence: cadenceConfig,
                    expirationNumberOfDays: type === NotificationName.expiringRequestReminder ?
                        expirationNumberOfDays
                        : null,
                    notificationConfiguration,
                });
                this.setNotificationCadence({ rpName, type, cadence });
                this.setLoadNotificationCadenceStatus({ rpName, type, status: OperationStatus.success });
            } catch (error) {
                this.setLoadNotificationCadenceStatus({ rpName, type, status: OperationStatus.error });
                await getPersistingErrorActions().showError(error);
            }
        },
        async updateNotificationCadence(
            payload: { rpName: string, type: NotificationName, cadence: NotificationCadence },
        ) {
            const { rpName, type, cadence } = payload;
            try {
                this.setUpdateNotificationCadenceStatus({ rpName, type, status: OperationStatus.loading });
                const [
                    { cadenceConfig },
                    { expirationNumberOfDays },
                    { notifications },
                ] = await Promise.all([
                    appOf(this).gatewayClient.tprmUi().getNotificationPreference(rpName, type),
                    appOf(this).gatewayClient.tprmUi().getTprmRequestsConfig(rpName),
                    appOf(this).gatewayClient.tprmUi().getNotificationConfigurations(rpName),
                ]);
                if (!cadenceConfig) {
                    throw new Error(`No cadence available for "${type}".`);
                }

                const input = buildCadenceInput(cadence);
                const promises: Promise<any>[] = [ Promise.resolve() ];
                const notificationType = getNotificationType(type);
                const currentConfig = notifications[notificationType];
                if (!currentConfig) {
                    throw new Error(`No notification configuration available for "${type}".`);
                }

                // Update expiration date if necessary
                if (
                    type === NotificationName.expiringRequestReminder &&
                cadence.daysBeforeCount != null &&
                cadence.daysBeforeCount !== expirationNumberOfDays
                ) {
                    promises.push(appOf(this).gatewayClient.tprmUi().updateTprmRequestsConfig(rpName, {
                        expirationNumberOfDays: cadence.daysBeforeCount,
                    }));
                }

                // SetRemindersModal emits via getFormValue(...), which maps a property absent from
                // the submitted value to null; neither flag is in buildReminderSchema's required list,
                // so null is reachable here and falls back to the stored value rather than being sent
                // as null in a PUT that replaces the whole configuration.
                const initEnabled = cadence.initEnabled ?? currentConfig.initEnabled;
                const remindersEnabled = cadence.remindersEnabled ?? currentConfig.remindersEnabled;
                if (initEnabled !== currentConfig.initEnabled || remindersEnabled !== currentConfig.remindersEnabled) {
                    promises.push(appOf(this).gatewayClient.tprmUi()
                        .updateNotificationConfiguration(rpName, notificationType, {
                            ...currentConfig,
                            initEnabled,
                            remindersEnabled,
                        }));
                }

                // Update cadence if necessary
                if (!isEqual(input, pick(cadenceConfig, Object.keys(input)))) {
                    promises.push(appOf(this).gatewayClient.tprmUi().updateCadence(rpName, cadenceConfig._id, input));
                }

                // Wait for all requests to complete
                await Promise.all(promises);

                this.setNotificationCadence({ rpName, type, cadence });
                this.setUpdateNotificationCadenceStatus({ rpName, type, status: OperationStatus.success });
            } catch (error) {
                this.setUpdateNotificationCadenceStatus({ rpName, type, status: OperationStatus.error });
                await getPersistingErrorActions().showError(error);
            }
        },
        async checkNotificationCadenceAvailability(
            payload: { rpName: string, type: NotificationName },
        ): Promise<boolean> {
            const { rpName, type } = payload;
            try {
                const { cadenceConfig } = await appOf(this).gatewayClient.tprmUi()
                    .getNotificationPreference(rpName, type);
                return Boolean(cadenceConfig);
            } catch {
                return false;
            }
        },
        async sendTestEmail(payload: {
            rpName: string;
            type: VerificationRequestType;
            to: string;
            cc?: string[];
            audience?: NotificationAudience;
        }) {
            const { rpName, type, to, cc, audience } = payload;
            try {
                this.setSendTestEmailStatus({ rpName, status: OperationStatus.loading });
                await appOf(this).gatewayClient.tprmUi().sendTestEmail(rpName, type, to, { cc, audience });
                this.setSendTestEmailStatus({ rpName, status: OperationStatus.success });
            } catch (error) {
                this.setSendTestEmailStatus({ rpName, status: OperationStatus.error });
                await getPersistingErrorActions().showError(error);
            }
        },
    },
});
